/**
 * F-074 — Texas Email Sequence Builder & Brevo Preview (E-022 FE, Batch-8).
 *
 * ACs: "Seven-touch cold/demo/proposal/follow-up-1..4 email sequence templates
 * (validated against brevoTemplateShape)" and "Preview each touch's
 * Brevo-rendered email (SB 38 / Houston Ch. 10 / Dallas Ch. 27 / US residency
 * topics)".
 *
 * The seven-touch sequence lives FE-side (LP has no cadence HTTP route — the
 * engine is lib-only and cron-driven) and every touch is checked against
 * validateBrevoTemplateShape(). The Brevo-rendered HTML binds to the nominated
 * EMAIL_SEQUENCE_PATHS.preview and degrades to the FE preview when the batch-8
 * cadence BE is unpublished.
 *
 * Market-gated to texas, registered at /texas/email-sequence (group 'other').
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import {
    SEQUENCE_TOUCH_ORDER,
    TX_EMAIL_TOPICS,
    buildSequence,
    emailSequenceApi,
    previewTouch,
    SequenceTouchKey,
} from '../../services/emailSequence';

export const TexasSequenceBuilder: React.FC = () => {
    const isTexas = getAppMarket() === 'texas';
    const [topicId, setTopicId] = useState<string>('sb38');
    const [activeKey, setActiveKey] = useState<SequenceTouchKey>('cold');
    const [brevoHtml, setBrevoHtml] = useState<string | null>(null);
    const [loadingBrevo, setLoadingBrevo] = useState(false);

    const touches = useMemo(() => buildSequence(topicId), [topicId]);
    const active = touches.find((t) => t.key === activeKey) || touches[0];
    const preview = useMemo(() => previewTouch(active, topicId), [active, topicId]);

    useEffect(() => {
        if (!isTexas) return;
        setBrevoHtml(null);
        setLoadingBrevo(true);
        emailSequenceApi
            .brevoPreview('seg-texas-marketing', active.key)
            .then((html) => setBrevoHtml(html))
            .finally(() => setLoadingBrevo(false));
    }, [isTexas, active, topicId]);

    if (!isTexas) return null;

    return (
        <div className="mx-auto max-w-6xl px-4 py-8" data-testid="texas-sequence-builder">
            <h1 className="text-2xl font-bold text-gray-900">Texas Email Sequence</h1>
            <p className="mt-1 text-sm text-gray-600">
                Seven-touch cold → demo → proposal → follow-up cadence, previewed as Brevo renders it.
            </p>

            <div className="mt-4">
                <div className="text-xs font-semibold uppercase text-gray-500">Texas topic</div>
                <div className="mt-1 flex flex-wrap gap-2">
                    {TX_EMAIL_TOPICS.map((topic) => (
                        <button
                            key={topic.id}
                            type="button"
                            data-testid="tx-topic-chip"
                            onClick={() => setTopicId(topic.id)}
                            className={`rounded-full px-3 py-1 text-xs font-medium ${
                                topicId === topic.id
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {topic.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
                <div>
                    <div className="text-xs font-semibold uppercase text-gray-500">Cadence</div>
                    <ol className="mt-2 space-y-2" data-testid="tx-touch-list">
                        {SEQUENCE_TOUCH_ORDER.map((key) => {
                            const touch = touches.find((t) => t.key === key);
                            if (!touch) return null;
                            const errors = previewTouch(touch, topicId).validationErrors;
                            return (
                                <li key={key}>
                                    <button
                                        type="button"
                                        data-testid="tx-touch-item"
                                        onClick={() => setActiveKey(key)}
                                        className={`w-full rounded-lg border px-3 py-2 text-left ${
                                            activeKey === key
                                                ? 'border-blue-400 bg-blue-50'
                                                : 'border-gray-200 bg-white hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-gray-900" data-testid="tx-touch-key">
                                                {key}
                                            </span>
                                            <span className="text-xs text-gray-500" data-testid="tx-touch-offset">
                                                day {touch.offsetDays}
                                            </span>
                                        </div>
                                        <div className="mt-0.5 text-xs text-gray-600">{touch.subject}</div>
                                        {errors.length > 0 && (
                                            <div className="mt-0.5 text-xs text-red-600" data-testid="tx-touch-invalid">
                                                template invalid: {errors.join(', ')}
                                            </div>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4" data-testid="tx-touch-preview">
                    <div className="flex items-center justify-between">
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                            Day {active.offsetDays} · {active.key}
                        </span>
                        {preview.topic && (
                            <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700" data-testid="tx-preview-topic">
                                {preview.topic.label}
                            </span>
                        )}
                    </div>

                    <div className="mt-3 text-xs uppercase text-gray-500">Subject</div>
                    <div className="text-sm font-semibold text-gray-900" data-testid="tx-preview-subject">
                        {preview.subject}
                    </div>

                    <div className="mt-3 text-xs uppercase text-gray-500">Heading</div>
                    <div className="text-sm font-bold text-gray-900">{preview.heading}</div>

                    {preview.topic && (
                        <>
                            <div className="mt-3 text-xs uppercase text-gray-500">Topic angle</div>
                            <div className="text-sm text-gray-700">{preview.topic.angle}</div>
                            <div className="mt-1 text-xs text-gray-500">{preview.topic.jurisdiction}</div>
                        </>
                    )}

                    <div className="mt-3 text-xs uppercase text-gray-500">Body points</div>
                    <ul className="mt-1 list-disc pl-5 text-sm text-gray-700">
                        {preview.points.map((point) => (
                            <li key={point}>{point}</li>
                        ))}
                    </ul>

                    <div className="mt-3 text-xs uppercase text-gray-500">UTM preview link</div>
                    <div className="break-all font-mono text-xs text-gray-600" data-testid="tx-utm-preview">
                        {preview.utm}
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="tx-brevo-preview-button"
                            onClick={() => {
                                setLoadingBrevo(true);
                                emailSequenceApi
                                    .brevoPreview('seg-texas-marketing', active.key)
                                    .then((html) => setBrevoHtml(html))
                                    .finally(() => setLoadingBrevo(false));
                            }}
                            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                        >
                            Preview in Brevo
                        </button>
                        {loadingBrevo && (
                            <span className="text-xs text-gray-500" data-testid="tx-brevo-loading">
                                Rendering…
                            </span>
                        )}
                    </div>

                    {brevoHtml ? (
                        <pre
                            className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs text-gray-700"
                            data-testid="tx-brevo-preview"
                        >
                            {brevoHtml}
                        </pre>
                    ) : (
                        !loadingBrevo && (
                            <p className="mt-3 text-xs text-gray-500" data-testid="tx-brevo-unavailable">
                                Brevo-rendered preview unavailable — showing the template the engine will render.
                            </p>
                        )
                    )}

                    {preview.validationErrors.length > 0 && (
                        <ul className="mt-3 text-xs text-red-600" data-testid="tx-validation-errors">
                            {preview.validationErrors.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TexasSequenceBuilder;
